import React, { useMemo } from 'react'
import { parser as pythonParser } from '@lezer/python'
import { highlightCode, tagHighlighter, tags as t } from '@lezer/highlight'

// Static syntax highlighting for fenced code in task docs.
//
// Deliberately NOT a CodeMirror instance: these blocks are read-only prose, and an
// editor here would bring a cursor, its own scroll container, and a second selection
// model that fights the doc-selection telemetry. `highlightCode` walks the same Lezer
// tree CodeMirror uses, so the colours agree with the editor without the machinery.
//
// Only `python` fences are tokenised. Bare fences in these docs are program OUTPUT
// ("Booting…", "Worker 1 done") or shell (`pip install trio`) — running those through
// a Python grammar paints keywords onto text that has none, which reads as noise.
const highlighter = tagHighlighter([
  { tag: [t.keyword, t.controlKeyword, t.moduleKeyword, t.operatorKeyword], class: 'tok-kw' },
  { tag: [t.definitionKeyword], class: 'tok-kw' },
  { tag: [t.comment, t.lineComment, t.blockComment], class: 'tok-com' },
  { tag: [t.string, t.special(t.string)], class: 'tok-str' },
  { tag: [t.number, t.bool, t.null], class: 'tok-num' },
  { tag: [t.function(t.variableName), t.function(t.definition(t.variableName))], class: 'tok-fn' },
  { tag: [t.definition(t.variableName)], class: 'tok-def' },
  { tag: [t.className, t.typeName, t.standard(t.variableName)], class: 'tok-typ' },
  { tag: [t.operator, t.punctuation, t.bracket], class: 'tok-op' }
])

const LANGS = new Set(['python', 'py', 'python3'])

function highlightPython (code) {
  const nodes = []
  let key = 0
  highlightCode(
    code,
    pythonParser.parse(code),
    highlighter,
    (text, classes) => {
      nodes.push(classes ? <span key={key++} className={classes}>{text}</span> : text)
    },
    () => { nodes.push('\n') }
  )
  return nodes
}

// The same, one array of nodes per line (a final newline ends the last line; it starts no new one).
function highlightPythonLines (code) {
  const lines = [[]]
  let key = 0
  highlightCode(
    code,
    pythonParser.parse(code),
    highlighter,
    (text, classes) => {
      lines[lines.length - 1].push(classes ? <span key={key++} className={classes}>{text}</span> : text)
    },
    () => { lines.push([]) }
  )
  if (lines.length > 1 && lines[lines.length - 1].length === 0) lines.pop()
  return lines
}

// lineNumbers (optional): each line in a row of its own (.md-line), numbered (.md-line-number,
// hidden from screen readers) - for code a message refers to by line, such as an exercise's
// provided code in a traceback. Without it, the block as it has always been.
export default function CodeBlock ({ code, lang, lineNumbers = false }) {
  const isPython = LANGS.has(String(lang || '').toLowerCase())
  // Parsing is pure and the doc never changes mid-task, so do it once per block
  // rather than on every parent render — the countdown re-renders this tree once a second.
  const content = useMemo(() => {
    if (!lineNumbers) return isPython ? highlightPython(code) : code
    const lines = isPython ? highlightPythonLines(code) : String(code).replace(/\n$/, '').split('\n').map((line) => [line])
    return lines.map((nodes, index) => (
      <span className="md-line" key={index}>
        <span className="md-line-number" aria-hidden="true">{index + 1}</span>
        <span className="md-line-text">{nodes}</span>
        {'\n'}
      </span>
    ))
  }, [code, isPython, lineNumbers])
  return (
    <pre className={`md-pre${isPython ? ' md-pre--code' : ''}${lineNumbers ? ' md-pre--numbered' : ''}`}>
      <code>{content}</code>
    </pre>
  )
}
